import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";

const ProcessSection = () => {
  const processSteps = [
    {
      step: 1,
      icon: "MessageCircle",
      title: "Primeiro Contato",
      description:
        "Entre em contato conosco via WhatsApp ou telefone. Nossa equipe está disponível para esclarecer suas dúvidas iniciais.",
      duration: "Imediato",
      details: [
        "Atendimento humanizado",
        "Esclarecimento de dúvidas",
        "Agendamento de consulta",
      ],
    },
    {
      step: 2,
      icon: "Search",
      title: "Análise Completa",
      description:
        "Realizamos uma análise detalhada da sua situação financeira atual, identificando oportunidades e soluções personalizadas.",
      duration: "24 horas",
      details: [
        "Análise de documentos",
        "Avaliação de crédito",
        "Identificação de oportunidades",
      ],
    },
    {
      step: 3,
      icon: "FileText",
      title: "Proposta Personalizada",
      description:
        "Apresentamos uma proposta sob medida para suas necessidades, com todas as condições claras e transparentes.",
      duration: "48 horas",
      details: [
        "Proposta detalhada",
        "Condições transparentes",
        "Simulação completa",
      ],
    },
    {
      step: 4,
      icon: "CheckCircle",
      title: "Aprovação e Liberação",
      description:
        "Após sua aprovação, cuidamos de toda a documentação e liberação dos recursos de forma rápida e segura.",
      duration: "Até 72 horas",
      details: [
        "Documentação simplificada",
        "Aprovação rápida",
        "Liberação garantida",
      ],
    },
  ];

  const guarantees = [
    {
      icon: "Shield",
      title: "Sem Taxas Antecipadas",
      description: "Você só paga após a aprovação e liberação do seu crédito",
    },
    {
      icon: "Clock",
      title: "Resposta em 24h",
      description: "Garantimos retorno sobre sua solicitação em até 24 horas",
    },
    {
      icon: "Lock",
      title: "Dados Protegidos",
      description:
        "Suas informações são tratadas com máxima segurança e sigilo",
    },
    {
      icon: "Users",
      title: "Atendimento Dedicado",
      description: "Um consultor exclusivo acompanha todo o seu processo",
    },
  ];

  const handleWhatsAppClick = () => {
    const message = encodeURIComponent(
      "Olá! Gostaria de iniciar o processo de análise financeira gratuita.",
    );
    window.open(`https://wa.me/553397070278?text=${message}`, "_blank");
  };

  return (
    <section
      id="como-funciona"
      className="py-20 bg-gradient-to-br from-slate-50 to-white"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Icon name="Settings" size={16} />
            <span>Como Funciona</span>
          </div>

          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            Processo Simples e Transparente
          </h2>

          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Em apenas 4 passos simples, você pode transformar sua situação
            financeira. Nosso processo é desenhado para ser rápido, seguro e
            completamente transparente
          </p>
        </div>

        {/* Process Timeline */}
        <div className="relative mb-20">
          {/* Desktop Timeline */}
          <div className="hidden lg:block">
            <div className="flex items-center justify-between mb-8">
              {processSteps?.map((step, index) => (
                <div key={step?.step} className="flex items-center">
                  <div className="flex flex-col items-center">
                    <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center text-white font-bold text-lg mb-4 shadow-lg">
                      {step?.step}
                    </div>
                    <div className="w-2 h-2 bg-primary rounded-full"></div>
                  </div>
                  {index < processSteps?.length - 1 && (
                    <div className="flex-1 h-0.5 bg-primary/30 mx-4 mt-8"></div>
                  )}
                </div>
              ))}
            </div>

            <div className="grid lg:grid-cols-4 gap-8">
              {processSteps?.map((step) => (
                <div
                  key={step?.step}
                  className="bg-white rounded-2xl p-6 shadow-subtle border"
                >
                  <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-4">
                    <Icon
                      name={step?.icon as any}
                      size={24}
                      className="text-primary"
                    />
                  </div>

                  <div className="space-y-4">
                    <div>
                      <h3 className="text-lg font-bold text-foreground mb-2">
                        {step?.title}
                      </h3>
                      <div className="inline-flex items-center space-x-1 bg-success/10 text-success px-2 py-1 rounded-full text-xs font-medium">
                        <Icon name="Clock" size={12} />
                        <span>{step?.duration}</span>
                      </div>
                    </div>

                    <p className="text-muted-foreground text-sm leading-relaxed">
                      {step?.description}
                    </p>

                    <ul className="space-y-2">
                      {step?.details?.map((detail, index) => (
                        <li
                          key={index}
                          className="flex items-center space-x-2 text-xs"
                        >
                          <Icon
                            name="Check"
                            size={12}
                            className="text-success flex-shrink-0"
                          />
                          <span className="text-muted-foreground">
                            {detail}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Mobile Timeline */}
          <div className="lg:hidden space-y-8">
            {processSteps?.map((step, index) => (
              <div key={step?.step} className="relative">
                <div className="flex items-start space-x-4">
                  <div className="flex flex-col items-center">
                    <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center text-white font-bold shadow-lg">
                      {step?.step}
                    </div>
                    {index < processSteps?.length - 1 && (
                      <div className="w-0.5 h-16 bg-primary/30 mt-4"></div>
                    )}
                  </div>

                  <div className="flex-1 bg-white rounded-xl p-6 shadow-subtle border">
                    <div className="flex items-center space-x-3 mb-3">
                      <Icon
                        name={step?.icon as any}
                        size={20}
                        className="text-primary"
                      />
                      <h3 className="text-lg font-bold text-foreground">
                        {step?.title}
                      </h3>
                    </div>

                    <div className="inline-flex items-center space-x-1 bg-success/10 text-success px-2 py-1 rounded-full text-xs font-medium mb-3">
                      <Icon name="Clock" size={12} />
                      <span>{step?.duration}</span>
                    </div>

                    <p className="text-muted-foreground text-sm mb-4">
                      {step?.description}
                    </p>

                    <ul className="space-y-2">
                      {step?.details?.map((detail, index) => (
                        <li
                          key={index}
                          className="flex items-center space-x-2 text-xs"
                        >
                          <Icon
                            name="Check"
                            size={12}
                            className="text-success flex-shrink-0"
                          />
                          <span className="text-muted-foreground">
                            {detail}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Guarantees Grid */}
        <div className="mb-16">
          <h3 className="text-2xl font-bold text-foreground text-center mb-12">
            Nossas Garantias para Você
          </h3>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {guarantees?.map((guarantee, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-6 text-center shadow-subtle border"
              >
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <Icon
                    name={guarantee?.icon as any}
                    size={24}
                    className="text-primary"
                  />
                </div>
                <h4 className="font-bold text-foreground mb-2">
                  {guarantee?.title}
                </h4>
                <p className="text-sm text-muted-foreground">
                  {guarantee?.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* FAQ Preview */}
        <div className="bg-white rounded-3xl p-8 md:p-12 shadow-subtle border mb-16">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold text-foreground mb-4">
              Dúvidas Frequentes
            </h3>
            <p className="text-muted-foreground">
              Esclarecemos as principais dúvidas sobre nosso processo
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <Icon
                  name="HelpCircle"
                  size={20}
                  className="text-primary flex-shrink-0 mt-1"
                />
                <div>
                  <h4 className="font-semibold text-foreground mb-1">
                    É realmente gratuita a análise?
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    Sim, a análise inicial é 100% gratuita e sem compromisso.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <Icon
                  name="HelpCircle"
                  size={20}
                  className="text-primary flex-shrink-0 mt-1"
                />
                <div>
                  <h4 className="font-semibold text-foreground mb-1">
                    Quanto tempo demora a aprovação?
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    Em média, 24 a 72 horas após o envio da documentação.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <Icon
                  name="HelpCircle"
                  size={20}
                  className="text-primary flex-shrink-0 mt-1"
                />
                <div>
                  <h4 className="font-semibold text-foreground mb-1">
                    Preciso sair de casa?
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    Não, todo o processo pode ser feito digitalmente.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <Icon
                  name="HelpCircle"
                  size={20}
                  className="text-primary flex-shrink-0 mt-1"
                />
                <div>
                  <h4 className="font-semibold text-foreground mb-1">
                    Meus dados estão seguros?
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    Sim, seguimos rigorosos protocolos de segurança e LGPD.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center">
          <div className="bg-gradient-to-r from-primary/5 to-secondary/5 rounded-3xl p-8 md:p-12">
            <h3 className="text-2xl md:text-3xl font-bold text-foreground mb-4">
              Pronto para Começar?
            </h3>
            <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">
              Inicie agora mesmo seu processo de transformação financeira. Nossa
              equipe está pronta para atendê-lo com toda a atenção que você
              merece.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                variant="default"
                size="lg"
                onClick={handleWhatsAppClick}
                iconName="MessageCircle"
                iconPosition="left"
                className="text-lg px-8 py-4 shadow-cta"
              >
                Iniciar Análise Gratuita
              </Button>

              <Button
                variant="outline"
                size="lg"
                onClick={() => {
                  const element = document.getElementById("contato");
                  if (element) {
                    element?.scrollIntoView({
                      behavior: "smooth",
                      block: "start",
                    });
                  }
                }}
                iconName="Phone"
                iconPosition="left"
                className="text-lg px-8 py-4"
              >
                Falar com Especialista
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProcessSection;
